import Big from 'big.js'
import {getCountryData} from 'countries-list'
import {format} from 'date-fns'

import FlightArrowSvg from '@/assets/flight/icons/flight-arrow.svg?react'
import passenger2Svg from '@/assets/flight/icons/pasenger2.svg'
import Loading from '@/components/Loading/Loading'
import Price from '@/components/Price'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import type {StateProvinceRequiredCountries} from '@/lib/flight/constants'
import {isStateProvinceRequiredCountry} from '@/lib/flight/constants'
import {getBooking} from '@/lib/flight/flightService'
import HeadTags from '@/lib/head/HeadTags'
import {isCountryCode} from '@/lib/locale/country'
import {REGIONS_MAP} from '@/lib/locale/regions'
import {FlightsDetailsRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {DisplayMoreInfoOld, DisplayMoreInfoOption} from '@/views/Flight/components/DisplayMoreInfo'
import NotFound from '@/views/NotFound/NotFound'

import style from './FlightDetails.module.scss'
import {STATUS_MAPPING} from './Flights'

export const PASSENGER_TYPE_MAP: Record<string, string> = {
  adult: 'Adult',
  child: 'Child',
  infant_in_seat: 'Infant in seat',
  infant_on_lap: 'Infant on lap',
}

function useFlightDetails(bookingId: string) {
  return useQuery({
    queryKey: ['flightDetails', bookingId],
    queryFn: async ({signal}) => getBooking({bookingId, signal}),
  })
}

export default memo(function FlightDetails() {
  const {bookingId} = FlightsDetailsRoute.useParams()
  const {data: booking, isLoading} = useFlightDetails(bookingId)

  const citiesText = (() => {
    const cities: string[] = []
    booking?.flightSegments.forEach(fs => {
      fs.flights.forEach(f => {
        const lastCity = cities[cities.length - 1]
        if (lastCity !== f.departure_airport.city) cities.push(f.departure_airport.city)
        if (lastCity !== f.arrival_airport.city) cities.push(f.arrival_airport.city)
      })
    })

    if (cities[cities.length - 1] === cities[0]) {
      cities.pop()
    }
    return cities.join(' - ')
  })()

  // eslint-disable-next-line @eslint-react/no-unnecessary-use-callback -- it's necessary
  const goBack = useCallback(() => {
    globalThis.history.back()
  }, [])

  if (isLoading) return <Loading label='Loading flight details...' />
  if (!booking) return <NotFound />

  const price = Big(booking.price)
  const discountValue = Big(booking.moreInfo.amountDiscount).times(-1)
  const roundUp = Big(booking.moreInfo.amountRoundUp)
  const total = price.plus(discountValue).add(roundUp)

  return (
    <>
      <HeadTags title={`Flight #${booking.id}`} />
      <PrimaryButton className={style.GoBack} onPress={goBack}>
        Go back
      </PrimaryButton>
      <div className={style.BookingDetails}>
        <div className={style.BookingHeader}>
          <h2 className={style.Heading}>
            <VisuallyHidden>Flight:</VisuallyHidden>
            {citiesText}
          </h2>
          <div
            className={style.BookingStatus}
            style={{color: STATUS_MAPPING[booking.status].color}}
          >
            <VisuallyHidden>Status:</VisuallyHidden>
            {STATUS_MAPPING[booking.status].text}
          </div>
          <div className={style.BookingHeaderDetails}>
            <div className={style.BookingDate}>
              Placed on {format(new Date(booking.createdAt), 'd MMM, yyyy')}
            </div>
            <div className={style.BookingNumber}>
              Booking number <span>{booking.id}</span>
            </div>
          </div>
        </div>
        <div className={style.FlightSegments}>
          {booking.flightSegments.map((flightSegment, idx) => {
            const firstFlight = flightSegment.flights[0]
            const lastFlight = flightSegment.flights[flightSegment.flights.length - 1]

            if (!firstFlight || !lastFlight) return null
            const durationText = `${Math.floor(flightSegment.total_duration / 60)}h ${flightSegment.total_duration % 60}m`
            const numOfStops = flightSegment.flights.length - 1

            return (
              <div className={style.FlightSegment} key={idx}>
                <div className={style.FlightSegmentHeader}>
                  <div className={style.FlightSegmentHeaderFromTo}>
                    From <span>{firstFlight.departure_airport.city}</span> to{' '}
                    <span>{lastFlight.arrival_airport.city}</span>
                  </div>
                  <div className={style.FlightSegmentHeaderTime}>
                    From{' '}
                    <span>
                      {format(
                        new Date(`${firstFlight.departure_airport.time} `),
                        'h:mma, MMM d, yyyy',
                      )}
                    </span>{' '}
                    to{' '}
                    <span>
                      {format(
                        new Date(`${lastFlight.arrival_airport.time} `),
                        'h:mma, MMM d, yyyy',
                      )}
                    </span>
                  </div>
                </div>
                <div className={style.FlightSegmentBody}>
                  <div className={style.FlightSegmentBodyDetails}>
                    <div className={style.FlightSegmentBodyDetailsHeader}>
                      <div className={style.FlightSegmentAirlineInfo}>
                        <img src={firstFlight.airline_logo} alt='' loading='lazy' />
                        <div>
                          <div className={style.FlightSegmentAirlineInfoSup}>
                            <VisuallyHidden>Airline:</VisuallyHidden>
                            {firstFlight.airline} - <VisuallyHidden>Travel class:</VisuallyHidden>
                            {firstFlight.travel_class}
                          </div>
                          <div className={style.FlightSegmentAirlineInfoSub}>
                            <VisuallyHidden>Flight number:</VisuallyHidden>
                            {firstFlight.flight_number}
                          </div>
                          <div className={style.FlightSegmentAirlineInfoSub}>
                            <VisuallyHidden>Duration:</VisuallyHidden>
                            {durationText} ({numOfStops} stop{numOfStops > 1 ? 's' : ''})
                          </div>
                        </div>
                      </div>
                      <div className={style.FlightSegmentPnr}>
                        PNR: <span>{flightSegment.pnr ?? 'N/A'}</span>
                      </div>
                    </div>
                    <ol className={style.Flights}>
                      {flightSegment.flights.map((flight, idx) => {
                        const durationText = `${Math.floor(flight.duration / 60)}h ${flight.duration % 60}m`
                        const rawLayoverDuration = flightSegment.layovers?.[idx - 1]?.duration
                        const layoverDuration = rawLayoverDuration
                          ? `${Math.floor(rawLayoverDuration / 60)}h ${rawLayoverDuration % 60}m`
                          : undefined

                        return (
                          <Fragment key={idx}>
                            {layoverDuration && (
                              <li className={style.Layover}>
                                Change plane at {flight.departure_airport.city} (
                                {flight.departure_airport.id}) in {layoverDuration}
                              </li>
                            )}
                            <li className={style.Flight}>
                              <div className={style.FlightDestination}>
                                <div className={style.FlightDestinationCity}>
                                  <VisuallyHidden as='h4'>Departure airport:</VisuallyHidden>
                                  {flight.departure_airport.id}
                                </div>
                                <div className={style.FlightDestinationTime}>
                                  <VisuallyHidden as='h4'>Departure time:</VisuallyHidden>
                                  {format(new Date(`${flight.departure_airport.time} `), 'hh:mm a')}
                                </div>
                                <div className={style.FlightDestinationDate}>
                                  <VisuallyHidden as='h4'>Departure date:</VisuallyHidden>
                                  {format(
                                    new Date(`${flight.departure_airport.time} `),
                                    'MMM dd, yyyy',
                                  )}
                                </div>
                              </div>
                              <div className={style.FlightDuration}>
                                <div className={style.FlightDurationText}>
                                  <VisuallyHidden as='h4'>Duration:</VisuallyHidden>
                                  {durationText}
                                </div>
                                <FlightArrowSvg />
                              </div>
                              <div className={style.FlightDestination}>
                                <div className={style.FlightDestinationCity}>
                                  <VisuallyHidden as='h4'>Arrival airport:</VisuallyHidden>
                                  {flight.arrival_airport.id}
                                </div>
                                <div className={style.FlightDestinationTime}>
                                  <VisuallyHidden as='h4'>Arrival time:</VisuallyHidden>
                                  {format(new Date(`${flight.arrival_airport.time} `), 'hh:mm a')}
                                </div>
                                <div className={style.FlightDestinationDate}>
                                  <VisuallyHidden as='h4'>Arrival date:</VisuallyHidden>
                                  {format(
                                    new Date(`${flight.arrival_airport.time} `),
                                    'MMM dd, yyyy',
                                  )}
                                </div>
                              </div>
                            </li>
                          </Fragment>
                        )
                      })}
                    </ol>
                  </div>
                </div>
                <div className={style.FlightSegmentBodyPassenger}>
                  <h3>Passenger information</h3>
                  <div className={style.FlightSegmentBodyPassengerDetails}>
                    {booking.passengers.map((passenger, idx) => {
                      const ticketNumber = flightSegment.paxTicketNumbers?.find(
                        ptn => ptn.paxId === passenger.id,
                      )?.ticketNumber

                      const countryName = isCountryCode(passenger.country)
                        ? getCountryData(passenger.country).name
                        : passenger.country

                      let stateProvinceName = ''
                      if (isStateProvinceRequiredCountry(passenger.country)) {
                        stateProvinceName =
                          REGIONS_MAP[passenger.country][
                            passenger.stateProvince as keyof (typeof REGIONS_MAP)[StateProvinceRequiredCountries]
                          ]
                      }

                      return (
                        <div
                          className={style.FlightSegmentBodyPassengerDetailsItem}
                          key={passenger.id}
                        >
                          <div className={style.FlightSegmentBodyPassengerDetailsLeft}>
                            <div>
                              <img src={passenger2Svg} alt='' />
                            </div>
                            <div>
                              <div className={style.FlightSegmentBodyPassengerDetailsLeftTop}>
                                Passenger {idx + 1}
                                {passenger.type && ` - ${PASSENGER_TYPE_MAP[passenger.type]}`}
                              </div>
                              <div className={style.FlightSegmentBodyPassengerDetailsLeftBottom}>
                                {passenger.firstName} {passenger.lastName} (
                                {format(new Date(`${passenger.dateOfBirth} `), 'MMM d, yyyy')})
                                {passenger.country && (
                                  <span className={style.PassengerLocation}>
                                    {` • ${countryName}`}
                                    {stateProvinceName && `, ${stateProvinceName}`}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className={style.FlightSegmentBodyPassengerDetailsRight}>
                            Ticket number: <span>{ticketNumber ?? 'N/A'}</span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {
          /* // if no moreInfo.option, then use old code, if not new code.  */
          booking.moreInfo.option ? (
            <DisplayMoreInfoOption option={booking.moreInfo.option} />
          ) : (
            <DisplayMoreInfoOld booking={booking} />
          )
        }

        <div className={style.BookingPricing}>
          <h3>Pricing Summary</h3>
          <div className={style.BookingPricingItem}>
            <div>
              Subtotal ({booking.passengers.length} passenger
              {booking.passengers.length > 1 ? 's' : ''})
            </div>
            <div>
              <Price
                price={price}
                options={{
                  fractionDigits: 2,
                  exactFractionDigits: true,
                }}
              />
            </div>
          </div>
          {roundUp.gt(0) && (
            <div className={style.BookingPricingItem}>
              <div>Donate change to NFB</div>
              <div>
                <Price
                  price={roundUp}
                  options={{
                    fractionDigits: 2,
                    exactFractionDigits: true,
                  }}
                />
              </div>
            </div>
          )}
          <div className={style.BookingPricingItem}>
            <div>Discount (total)</div>
            <div className={discountValue.lt(0) ? style.GoodValue : undefined}>
              <Price
                price={discountValue}
                options={{
                  fractionDigits: 2,
                  exactFractionDigits: true,
                }}
              />
            </div>
          </div>
          <div className={clsx(style.BookingPricingItem, style.BookingPricingItemTotal)}>
            <div>Total</div>
            <div>
              <Price
                price={total}
                options={{
                  fractionDigits: 2,
                  exactFractionDigits: true,
                }}
              />
            </div>
          </div>
        </div>
        <div className={style.BookingContact}>
          <h3>Contact information</h3>
          <div className={style.BookingContactDetails}>
            <div className={style.BookingContactDetailsItem}>
              <div>Full name</div>
              <div>
                {booking.contactInfo.firstName} {booking.contactInfo.lastName}
              </div>
            </div>
            <div className={style.BookingContactDetailsItem}>
              <div>Phone</div>
              <div>{booking.contactInfo.phoneNumber}</div>
            </div>
            <div className={style.BookingContactDetailsItem}>
              <div>Email</div>
              <div>{booking.contactInfo.email}</div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
})
